/**
 * Cache helpers after creating a user (search lists + API payload unwrap).
 */

import type { QueryClient } from '@tanstack/react-query';
import type { Student } from '@/types/inperson/student.types';
import type { Volunteer } from '@/types/volunteer.types';
import { isProfessorUser, type UserWithProfile, type UsersSearchPage } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Prepend the new user to side-column search caches (does not add them to Participants). */
export function prependUserToUsersSearchCaches(
  queryClient: QueryClient,
  role: 'tutor' | 'student',
  user: UserWithProfile,
): void {
  if (!user.id) return;

  queryClient.setQueriesData<UsersSearchPage>({ queryKey: ['users-search', role] }, (old) => {
    if (!old?.data?.length) {
      return { data: [user], total: 1, hasMore: false };
    }
    if (old.data.some((u) => u.id === user.id)) return old;
    return {
      ...old,
      data: [user, ...old.data],
      total: old.total + 1,
    };
  });
}

/** Unwrap POST /users payload when the user is nested. */
export function extractCreatedUserFromApiResponse<T extends { id: string }>(payload: unknown): T {
  if (!payload || typeof payload !== 'object') {
    throw new Error(getErrorMessage('invalidApiResponse'));
  }

  const record = payload as Record<string, unknown>;

  if (record.user && typeof record.user === 'object' && record.user !== null) {
    const nested = record.user as T;
    if (nested.id) return nested;
  }

  if (typeof record.id === 'string' && record.id.length > 0) {
    return payload as T;
  }

  throw new Error(getErrorMessage('createdUserMissingId'));
}

/** Map a volunteer list row to {@link UserWithProfile} for session pickers. */
export function volunteerToUserWithProfile(volunteer: Volunteer): UserWithProfile {
  const userRoles = volunteer.userRoles?.map((ur) => ({
    roleId: ur.roleId,
    role: {
      id: ur.roleId,
      code: ur.role.code,
      label: ur.role.label,
      roleGroup: 'volunteers',
    },
  }));

  const profile: UserWithProfile = {
    id: volunteer.id,
    companyId: volunteer.companyId,
    firebaseUid: volunteer.firebaseUid ?? '',
    email: volunteer.email,
    firstName: volunteer.firstName,
    lastName: volunteer.lastName,
    displayName: volunteer.displayName,
    photoUrl: volunteer.photoUrl,
    phone: volunteer.phone,
    status: volunteer.status,
    hoursDone: volunteer.hoursDone,
    managedById: volunteer.managedById,
    managedBy: volunteer.managedBy,
    createdAt: volunteer.createdAt ?? new Date(),
    updatedAt: volunteer.updatedAt ?? new Date(),
    userRoles,
    userProfiles: volunteer.userProfiles?.map((p) => ({
      ...p,
      id: p.profileId ?? undefined,
      vscReportsFrequencies: p.vscReportsFrequencies
        ? {
            daily: p.vscReportsFrequencies.daily,
            weekly: p.vscReportsFrequencies.weekly,
            monthly: false,
          }
        : undefined,
    })),
    currentRole: 'volunteer',
  };

  profile.currentRole = isProfessorUser(profile) ? 'tutor' : 'volunteer';
  return profile;
}

/** Map an in-person student list row to {@link UserWithProfile}. */
export function studentToUserWithProfile(student: Student): UserWithProfile {
  return {
    id: student.id,
    companyId: student.companyId,
    firebaseUid: '',
    email: student.email,
    firstName: student.firstName,
    lastName: student.lastName,
    displayName: student.displayName,
    photoUrl: student.photoUrl,
    phone: student.phone,
    status: student.status,
    createdAt: student.createdAt ? new Date(student.createdAt) : new Date(),
    updatedAt: student.updatedAt ? new Date(student.updatedAt) : new Date(),
    currentRole: 'student',
    userProfiles: student.userProfiles?.map((p) => ({
      ...p,
      id: p.profileId ?? undefined,
    })),
  };
}
